import { CircleSlash, KeyRound } from "lucide-react";
import type { ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import { BREB_KEY_REQUIRED_REASON } from "../../constants/cryptoSwaps.constants";
import type { UseCryptoSellFormResult } from "../../hooks/useCryptoSellForm";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSavedWallet } from "../../types/cryptoSwaps.types";
import { NetworkPicker } from "../shared/NetworkPicker";
import { DestinationSection } from "../swap/DestinationSection";
import { SellAmountFields } from "./SellAmountFields";
import { SellPayoutKey } from "./SellPayoutKey";

export interface SellCardProps {
  form: UseCryptoSellFormResult;
  /** The staff organization picker, rendered above everything it scopes. */
  organizationSlot?: ReactNode;
  disabled?: boolean;
  /** Offered when the sale is blocked only because the organization has no key to be paid to. */
  onCreateBrebKey?: () => void;
  /** The organization's own wallets: the deposit comes from one and returns to it. */
  savedWallets?: OtcSavedWallet[];
  canSaveWallet?: boolean;
  onManageSavedWallets?: () => void;
  volumeDiscount?: ReactNode;
}

/** How much, on which chain, from which wallet: one card, since every extra screen spends the countdown. */
export function SellCard({
  form,
  organizationSlot,
  disabled,
  onCreateBrebKey,
  savedWallets,
  canSaveWallet,
  onManageSavedWallets,
  volumeDiscount,
}: SellCardProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const { pair } = form;
  if (!pair) return null;

  const locked = disabled || !form.organizationReady || !form.pairAvailable;

  return (
    <ColocaSurface padding="none" className="overflow-hidden rounded-3xl">
      {organizationSlot && <div className="border-b border-border p-6">{organizationSlot}</div>}

      {!pair.available && (
        <div className="border-b border-border p-6">
          <ColocaAlert
            variant="secondary"
            icon={CircleSlash}
            heading={t("sell_unavailable_title")}
            text={labels.sellUnavailableReason(pair.unavailable_reason, pair.send_currency)}
            actions={
              pair.unavailable_reason === BREB_KEY_REQUIRED_REASON && onCreateBrebKey ? (
                <ColocaButton size="sm" icon={KeyRound} onClick={onCreateBrebKey}>
                  {t("pair_breb_key_create")}
                </ColocaButton>
              ) : undefined
            }
          />
        </div>
      )}

      <div className="border-b border-border p-5 sm:p-6">
        <SellAmountFields
          pair={pair}
          sendField={form.sendField}
          receiveField={form.receiveField}
          onSendChange={form.setSendAmount}
          onReceiveChange={form.setReceiveAmount}
          sendBounds={form.sendBounds}
          receiveBounds={form.receiveBounds}
          sendIssue={form.sendIssue}
          receiveIssue={form.receiveIssue}
          quote={form.quote}
          isPricing={form.isPricing}
          networkLabel={labels.networkShort(form.network)}
          disabled={locked}
        />
      </div>

      {volumeDiscount && <div className="border-b border-border p-5 sm:p-6">{volumeDiscount}</div>}

      {form.networks.length > 0 && (
        <div className="border-b border-border p-5 sm:p-6">
          <NetworkPicker
            networks={form.networks}
            value={form.network}
            onChange={form.selectNetwork}
            title={t("sell_network_title")}
            hint={t("sell_network_hint")}
            disabled={locked}
          />
        </div>
      )}

      <div className="p-5 sm:p-6">
        <DestinationSection
          variant="refund"
          network={form.network}
          asset={pair.send_currency}
          onChange={form.setRefund}
          disabled={disabled}
          savedWallets={savedWallets}
          canSave={canSaveWallet}
          onManageSaved={onManageSavedWallets}
        />
      </div>

      {pair.payout_key && (
        <div className="border-t border-border p-5 sm:p-6">
          <SellPayoutKey keyValue={pair.payout_key} />
        </div>
      )}
    </ColocaSurface>
  );
}
